import type { Channel } from 'pusher-js'
import Pusher from 'pusher-js'
import api, { axiosInstance } from '@api'
import type { PushEventRef, PushPayload } from '@/types/push-events'

Pusher.logToConsole = import.meta.env.VITE_PUSHER_DEBUG === 'true'

export enum PushChannelPrefix {
  Workspace = 'private-data-ws-',
  User = 'private-data-user-',
  Auction = 'private-data-hash-',
  BookingLink = 'private-data-link-hash-',
  Booking = 'private-data-booking-hash-',
  Portal = 'private-data-otp-hash-',
}

type ChannelEntry = { channel: Channel; refs: number }
type ChannelAuthorizationParams = { socketId: string; channelName: string }

let _pusher: Pusher | null = null
const _channels = new Map<string, ChannelEntry>()

// Bumped by clearPusher, so a consumer still mounted after logout rebinds to the next client.
const _clientGeneration = ref(0)

export function usePushChannelEvent<T>(
  channelName: MaybeRefOrGetter<string | null>,
  event: string,
): PushEventRef<T> {
  const resultRef = ref<PushPayload<T>>()
  let bound: { name: string; channel: Channel } | null = null

  const callback = (data: PushPayload<T>) => {
    resultRef.value = data
  }

  function unbind() {
    if (!bound)
      return
    bound.channel.unbind(event, callback)
    releaseChannel(bound.name, bound.channel)
    bound = null
  }

  watch(() => [toValue(channelName), _clientGeneration.value] as const, ([name]) => {
    unbind()

    const channel = name ? acquireChannel(name) : null
    if (name && channel) {
      channel.bind(event, callback)
      bound = { name, channel }
    }
  }, { immediate: true })

  tryOnScopeDispose(unbind)

  return resultRef
}

export function usePusherConnected(): Ref<boolean> {
  const isOnline = ref(false)
  let pusher: Pusher | null = null

  const callback = (state: { previous: string; current: string }) => {
    isOnline.value = state.current === 'connected'
  }

  function unbind() {
    pusher?.connection.unbind('state_change', callback)
  }

  watch(_clientGeneration, () => {
    unbind()
    pusher = getOrCreatePusher()
    isOnline.value = pusher?.connection.state === 'connected'
    pusher?.connection.bind('state_change', callback)
  }, { immediate: true })

  tryOnScopeDispose(unbind)

  return isOnline
}

export function getActivePusherSocketId(): string | undefined {
  return _pusher?.connection.socket_id
}

export function clearPusher() {
  _channels.forEach(({ channel }, name) => {
    channel.unbind_all()
    _pusher?.unsubscribe(name)
  })
  _channels.clear()
  _pusher?.unbind_all()
  _pusher?.disconnect()
  _pusher = null
  _clientGeneration.value++
}

function acquireChannel(name: string): Channel | null {
  const entry = _channels.get(name)
  if (entry) {
    entry.refs++

    return entry.channel
  }

  const channel = getOrCreatePusher()?.subscribe(name)
  if (!channel)
    return null

  _channels.set(name, { channel, refs: 1 })

  return channel
}

// Deferred so a route change that unmounts one consumer and mounts the next keeps the subscription.
function releaseChannel(name: string, channel: Channel) {
  const entry = _channels.get(name)
  if (entry?.channel !== channel)
    return

  entry.refs--
  queueMicrotask(() => {
    if (entry.refs > 0 || _channels.get(name) !== entry)
      return
    _channels.delete(name)
    _pusher?.unsubscribe(name)
  })
}

function getOrCreatePusher(): Pusher | null {
  if (_pusher)
    return _pusher

  const pusherKey = import.meta.env.VITE_PUSHER_KEY
  if (!pusherKey)
    return null

  _pusher = new Pusher(pusherKey, {
    cluster: import.meta.env.VITE_PUSHER_CLUSTER,
    channelAuthorization: {
      // Required by the types but unused: customHandler picks the endpoint per room in authorizeChannel.
      endpoint: axiosInstance.defaults.baseURL + `/api/v1/push/authorize/`,
      transport: 'ajax',
      customHandler: (params, callback) => {
        authorizeChannel(params)
          .then(response => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(null, response.data)
          })
          .catch(() => {
            // eslint-disable-next-line promise/no-callback-in-promise
            callback(new Error('Authorization failed'), null)
          })
      },
    },
  })

  return _pusher
}

const BY_HASH_CHANNEL_PREFIXES = [PushChannelPrefix.Auction, PushChannelPrefix.BookingLink, PushChannelPrefix.Booking]

function authorizeChannel(params: ChannelAuthorizationParams) {
  if (params.channelName.startsWith(PushChannelPrefix.Portal))
    return api.push.pushAuthorizeByOtpHashCreate(params, { skipAuthToken: true })
  if (BY_HASH_CHANNEL_PREFIXES.some(prefix => params.channelName.startsWith(prefix)))
    return api.push.pushAuthorizeByHashCreate(params, { skipAuthToken: true })

  return api.push.pushAuthorizeCreate(params)
}
